// Synthesized sound effects (Web Audio, no files). Every sound is built from oscillators and one shared
// noise buffer, with short ramped envelopes so nothing clicks. Without an AudioContext (old browser,
// blocked, node tests) every method is a silent no-op; nothing here ever throws into the game loop.
import type { SimEvent } from '../shared/types';
import type { EventType, PowerUpType } from '../shared/config';

const MUTE_KEY = 'cg.muted';
const DEFAULT_VOLUME = 0.5;
const MAX_VOICES = 24; // simultaneous one-shot sources; the boost loop is not counted
const EAT_GAP = 1 / 12; // eat pops are rate-limited to ~12/s
const EJECT_GAP = 0.08; // eject fires every 0.1 s while held
const STREAK_GAP = 0.6; // a pause this long (s) restarts the rising eat pitch
const COMBO_WINDOW = 4; // seconds, same window as the sim's kill combo
const RESUME_GRACE_MS = 400; // right after resume() in a gesture, schedule anyway (it starts a moment later)
const EPS = 0.0001; // exponential ramps can't reach 0
const LEAD = 0.01; // schedule slightly ahead so envelopes never start mid-ramp
const BOOST_LEVEL = 0.09; // continuous, so kept well under the one-shots

const hz = (midi: number) => 440 * Math.pow(2, (midi - 69) / 12);
const clamp = (v: number, a: number, b: number) => Math.max(a, Math.min(b, v));
const rand = (a: number, b: number) => a + Math.random() * (b - a);
const nowMs = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());
// major pentatonic: however high the eat streak climbs, every pop lands on a pleasant note
const PENTA = [0, 2, 4, 7, 9, 12, 14, 16];

// per power-up arpeggio: same bright idea, different colour
const PICKUP: Record<PowerUpType, { root: number; steps: number[]; wave: OscillatorType; gap: number; detune: number }> = {
  speed:  { root: 79, steps: [0, 4, 7, 12, 16], wave: 'triangle', gap: 0.032, detune: 0 }, // quick zippy run
  shield: { root: 72, steps: [0, 7, 12, 19], wave: 'triangle', gap: 0.06, detune: 0 },     // open fifths, sturdy
  magnet: { root: 76, steps: [0, 5, 9, 14], wave: 'sine', gap: 0.05, detune: 6 },          // sweet fourths
  ghost:  { root: 74, steps: [0, 4, 8, 12], wave: 'sine', gap: 0.07, detune: 16 },         // augmented + detuned: eerie
};
// event fanfares: three brassy notes, the last one held
const FANFARE: Record<EventType, number[]> = {
  rush: [67, 72, 76],  // G C E: happy
  swarm: [62, 65, 69], // D F A: minor, watch your step
  gold: [72, 76, 79],  // C E G, plus a high bell
};

interface Env { at?: number; a?: number; h?: number; d: number; peak: number } // offset, attack, hold, decay (s)
interface FilterOpts { type: BiquadFilterType; f0: number; f1?: number; q?: number; sweep?: number }
interface ToneOpts extends Env { wave?: OscillatorType; f0: number; f1?: number; sweep?: number; detune?: number; filter?: FilterOpts }
interface NoiseOpts extends Env { filter: FilterOpts }
interface Voice { end: number; env: GainNode; src: AudioScheduledSourceNode }
interface Loop { gain: GainNode; srcs: AudioScheduledSourceNode[] }
interface KV { getItem(k: string): string | null; setItem(k: string, v: string): void }

export interface SoundOptions {
  createContext?: () => AudioContext | null; // default: window.AudioContext / webkitAudioContext
  storage?: KV | null; // default: localStorage
}

function defaultContext(): AudioContext | null {
  const g = globalThis as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext };
  const AC = g.AudioContext ?? g.webkitAudioContext;
  if (!AC) return null;
  try { return new AC({ latencyHint: 'interactive' }); } catch { /* old Safari: no options */ }
  try { return new AC(); } catch { return null; }
}
function defaultStorage(): KV | null {
  try { return globalThis.localStorage ?? null; } catch { return null; }
}
// freeze a param where it is right now so a new ramp starts from the current value (no jump)
function hold(p: AudioParam, t: number): void {
  if (typeof p.cancelAndHoldAtTime === 'function') { p.cancelAndHoldAtTime(t); return; }
  const v = p.value;
  p.cancelScheduledValues(t);
  p.setValueAtTime(v, t);
}

export class Sound {
  private ctx: AudioContext | null = null;
  private failed = false; // AudioContext missing or refused: stop trying
  private bus: GainNode | null = null; // every voice goes here -> compressor -> master
  private master: GainNode | null = null;
  private noiseBuf: AudioBuffer | null = null;
  private voices: Voice[] = []; // oldest first
  private loop: Loop | null = null;
  private boosting = false;
  private resumedAt = -Infinity;
  private isMuted: boolean;
  private vol = DEFAULT_VOLUME;
  private lastEat = -Infinity;
  private lastEject = -Infinity;
  private streak = 0;
  private combo = { n: 0, until: 0 };
  private readonly makeCtx: () => AudioContext | null;
  private readonly store: KV | null;

  constructor(opts: SoundOptions = {}) {
    this.makeCtx = opts.createContext ?? defaultContext;
    this.store = opts.storage !== undefined ? opts.storage : defaultStorage();
    this.isMuted = this.read(MUTE_KEY) === '1';
  }

  // ---------- controls ----------
  // Call from every user gesture (pointerdown / keydown / touchend): creates the context the first time,
  // resumes it whenever the browser suspended it. Cheap when already running.
  unlock(): void {
    this.run(() => {
      if (!this.ctx && !this.failed) this.init();
      const c = this.ctx;
      if (!c) return;
      if (c.state === 'running') { this.resync(); return; }
      if (c.state === 'closed') return;
      this.resumedAt = nowMs();
      const p = c.resume();
      if (p && typeof p.then === 'function') p.then(() => this.run(() => this.resync()), () => {});
    });
  }
  get muted(): boolean { return this.isMuted; }
  set muted(m: boolean) { this.setMuted(m); }
  setMuted(m: boolean): void {
    this.isMuted = !!m;
    this.write(MUTE_KEY, this.isMuted ? '1' : '0');
    this.run(() => {
      this.applyLevel();
      if (this.isMuted) this.stopLoop(); else this.resync();
    });
  }
  get volume(): number { return this.vol; }
  set volume(v: number) {
    if (typeof v !== 'number' || !Number.isFinite(v)) return;
    this.vol = clamp(v, 0, 1);
    this.run(() => this.applyLevel());
  }
  // edge-triggered: safe to call every frame with the held state
  setBoosting(on: boolean): void {
    on = !!on;
    if (on === this.boosting) return;
    this.boosting = on;
    this.run(() => { if (on) this.startLoop(); else this.stopLoop(); });
  }

  // Map one drained batch of sim events to sounds: my own actions, plus global events.
  // Each sound plays at most once per batch, so a burst of events never stacks up.
  onSimEvents(events: SimEvent[], myId: number): void {
    if (!events || !events.length) return;
    this.run(() => {
      const c = this.ready();
      if (!c) return;
      let eat = 0, split = false, eject = false, pop = false, died = false;
      let first = false, milestone = false, unlocked = false;
      let killCombo = 0, goldMine = false, goldOther = false;
      let start: EventType | null = null, end: { type: EventType; escaped: boolean } | null = null;
      const picks = new Set<PowerUpType>();
      for (const e of events) {
        if (!e) continue;
        switch (e.t) {
          case 'eat': if (e.player === myId && e.mass > 0) eat += e.mass; break;
          case 'split': if (e.player === myId) split = true; break;
          case 'eject': if (e.player === myId) eject = true; break;
          case 'virusPop': if (e.player === myId) pop = true; break;
          case 'pickup': if (e.player === myId) picks.add(e.type); break;
          case 'kill':
            if (e.killer === myId) { killCombo = Math.max(killCombo, e.combo || 1); if (e.golden) goldMine = true; }
            else if (e.golden) goldOther = true;
            break;
          case 'death': if (e.player === myId) died = true; break;
          case 'eventStart': start = e.type; break;
          case 'eventEnd': end = { type: e.type, escaped: !!e.goldenEscaped }; break;
          case 'unlock': if (e.player === myId) unlocked = true; break;
          case 'first': if (e.player === myId) first = true; break;
          case 'milestone': if (e.player === myId) milestone = true; break;
        }
      }
      if (killCombo) this.combo = { n: killCombo, until: c.currentTime + COMBO_WINDOW };
      if (died) this.death();
      else {
        if (killCombo) this.kill(killCombo, goldMine);
        else if (eat > 0) this.eat(eat); // the kill crunch already covers this step's eating
        if (pop) this.virusPop(); else if (split) this.split();
        if (eject) this.eject();
        for (const p of picks) this.pickup(p);
        if (first) this.sparkle(true, 0.05);
        else if (milestone || unlocked) this.sparkle(unlocked, 0.05);
      }
      if (start) this.fanfare(start);
      else if (end) { if (!(end.type === 'gold' && goldMine)) this.eventEnd(end.escaped); } // jackpot already played
      else if (goldOther) this.eventEnd(false); // someone else ate the Golden Blob
    });
  }

  // ---------- sound effects (public so the UI shell can play them directly) ----------
  // soft pop; pitch climbs the scale while eating fast and with a live kill combo
  eat(mass = 1): void {
    this.run(() => {
      const c = this.ready();
      if (!c) return;
      const now = c.currentTime;
      if (now - this.lastEat < EAT_GAP) return;
      const t = this.begin(1, true);
      if (t < 0) return;
      this.streak = now - this.lastEat < STREAK_GAP ? this.streak + 1 : 0;
      this.lastEat = now;
      const combo = now < this.combo.until ? this.combo.n : 0;
      const top = PENTA.length - 1;
      let i = Math.min(top, (this.streak >> 1) + Math.min(combo, 3));
      if (i === top && Math.random() < 0.5) i--; // at the top, wobble between the two highest notes
      const f = hz(71 + PENTA[i]) * Math.pow(2, rand(-0.012, 0.012)); // from B4 up, slightly detuned each time
      const peak = 0.045 + 0.025 * Math.min(1, (Number.isFinite(mass) ? mass : 1) / 12);
      this.tone(t, { wave: 'sine', f0: f * 1.45, f1: f, sweep: 0.025, a: 0.003, d: 0.07, peak });
    });
  }
  // filtered noise sweeping up, with a faint rising sine for body
  split(): void {
    this.run(() => {
      const t = this.begin(2);
      if (t < 0) return;
      this.noise(t, { a: 0.03, d: 0.2, peak: 0.32, filter: { type: 'bandpass', f0: 350, f1: 2800, q: 1.4, sweep: 0.2 } });
      this.tone(t, { wave: 'sine', f0: 220, f1: 520, sweep: 0.16, a: 0.02, d: 0.14, peak: 0.07 });
    });
  }
  // short dull puff
  eject(): void {
    this.run(() => {
      const c = this.ready();
      if (!c || c.currentTime - this.lastEject < EJECT_GAP) return;
      const t = this.begin(2, true);
      if (t < 0) return;
      this.lastEject = c.currentTime;
      this.noise(t, { a: 0.004, d: 0.07, peak: 0.12, filter: { type: 'lowpass', f0: 1400, f1: 350, q: 0.9 } });
      this.tone(t, { wave: 'sine', f0: 300, f1: 160, a: 0.003, d: 0.05, peak: 0.04 });
    });
  }
  // burst: noisy crack, a falling pop and a low thump
  virusPop(): void {
    this.run(() => {
      const t = this.begin(3);
      if (t < 0) return;
      this.noise(t, { a: 0.002, d: 0.22, peak: 0.2, filter: { type: 'bandpass', f0: 2400, f1: 500, q: 0.8 } });
      this.tone(t, { wave: 'sine', f0: 1000, f1: 180, sweep: 0.06, a: 0.002, d: 0.08, peak: 0.12 });
      this.tone(t, { wave: 'sine', f0: 170, f1: 45, a: 0.004, d: 0.32, peak: 0.22 });
    });
  }
  // bright arpeggio with a shimmer an octave above the last note
  pickup(type: PowerUpType): void {
    this.run(() => {
      const p = PICKUP[type] ?? PICKUP.speed, n = p.steps.length;
      const t = this.begin(n + 1);
      if (t < 0) return;
      p.steps.forEach((s, i) => this.tone(t, {
        wave: p.wave, f0: hz(p.root + s), detune: i % 2 ? p.detune : -p.detune,
        at: i * p.gap, a: 0.004, d: i === n - 1 ? 0.35 : 0.14, peak: 0.1,
      }));
      this.tone(t, { wave: 'sine', f0: hz(p.root + p.steps[n - 1] + 12), at: (n - 1) * p.gap, a: 0.01, d: 0.4, peak: 0.035 });
    });
  }
  // crunch + rising chime; a combo makes the chime longer, higher and louder
  kill(combo = 1, golden = false): void {
    this.run(() => {
      const k = clamp(Math.round(combo) || 1, 1, 5), notes = Math.min(k + 1, 5);
      const t = this.begin(3 + notes);
      if (t < 0) return;
      this.noise(t, { a: 0.002, d: 0.06, peak: 0.2, filter: { type: 'bandpass', f0: 2200, q: 0.9 } });
      this.noise(t, { a: 0.003, d: 0.12, peak: 0.16 + k * 0.02, filter: { type: 'lowpass', f0: 900, f1: 250 } });
      this.tone(t, { wave: 'square', f0: 150, f1: 55, a: 0.003, d: 0.1, peak: 0.07, filter: { type: 'lowpass', f0: 1200, q: 0.7 } });
      const root = 76 + (k - 1) * 2, steps = [0, 4, 7, 12, 16], gap = 0.07 - k * 0.006;
      for (let i = 0; i < notes; i++) {
        this.tone(t, { wave: 'sine', f0: hz(root + steps[i]), at: 0.05 + i * gap, a: 0.004, d: i === notes - 1 ? 0.4 : 0.16, peak: 0.08 + k * 0.012 });
      }
      if (golden) this.jackpot(0.05 + notes * gap);
    });
  }
  // Golden Blob eaten: coins tumbling up, then a warm major chord
  jackpot(delay = 0): void {
    this.run(() => {
      const coins = [76, 79, 84, 88, 91, 96], chord = [72, 76, 79, 84];
      const t = this.begin(coins.length + chord.length);
      if (t < 0) return;
      coins.forEach((m, i) => this.tone(t, {
        wave: 'square', f0: hz(m), at: delay + i * 0.055, a: 0.003, d: 0.12, peak: 0.035,
        filter: { type: 'lowpass', f0: 4500, q: 0.5 },
      }));
      const at = delay + coins.length * 0.055;
      chord.forEach((m, i) => this.tone(t, { wave: 'triangle', f0: hz(m), detune: (i - 1.5) * 4, at, a: 0.012, h: 0.18, d: 0.7, peak: 0.07 }));
    });
  }
  // sad falling 'wah': a triangle and its sub-octave slide two octaves down while a low-pass closes
  death(): void {
    this.boosting = false; // the rumble dies with you; the next setBoosting(true) starts it again
    this.run(() => {
      this.stopLoop();
      const t = this.begin(3);
      if (t < 0) return;
      this.tone(t, { wave: 'triangle', f0: hz(67), f1: hz(43), a: 0.02, h: 0.1, d: 0.75, peak: 0.13, filter: { type: 'lowpass', f0: 2400, f1: 300, q: 1 } });
      this.tone(t, { wave: 'sine', f0: hz(55), f1: hz(31), a: 0.02, h: 0.1, d: 0.75, peak: 0.09 });
      this.noise(t, { a: 0.004, d: 0.2, peak: 0.08, filter: { type: 'lowpass', f0: 700, f1: 120 } });
    });
  }
  // three brassy notes: two detuned saws per note through a low-pass that opens on the attack
  fanfare(type: EventType): void {
    this.run(() => {
      const notes = FANFARE[type] ?? FANFARE.rush, bell = type === 'gold';
      const t = this.begin(notes.length * 2 + (bell ? 1 : 0));
      if (t < 0) return;
      notes.forEach((m, i) => {
        const last = i === notes.length - 1;
        for (const detune of [-7, 7]) {
          this.tone(t, {
            wave: 'sawtooth', f0: hz(m), detune, at: i * 0.12, a: 0.018, h: last ? 0.3 : 0.06, d: last ? 0.35 : 0.08, peak: 0.05,
            filter: { type: 'lowpass', f0: 500, f1: 2600, q: 1.6, sweep: 0.06 },
          });
        }
      });
      if (bell) this.tone(t, { wave: 'sine', f0: hz(84), at: 0.24, a: 0.01, h: 0.2, d: 0.6, peak: 0.05 });
    });
  }
  // soft two-note close; a droopy 'womp' when the Golden Blob got away
  eventEnd(escaped = false): void {
    this.run(() => {
      const notes = escaped ? [67, 63] : [72, 67];
      const t = this.begin(2);
      if (t < 0) return;
      notes.forEach((m, i) => this.tone(t, {
        wave: 'triangle', f0: hz(m), f1: escaped && i ? hz(m - 1) : undefined,
        at: i * 0.14, a: 0.01, h: 0.05, d: 0.3, peak: 0.06,
      }));
    });
  }
  // milestone: a few high twinkles; big (#1, power-ups unlocked): more of them over a soft chord
  sparkle(big = false, delay = 0): void {
    this.run(() => {
      const n = big ? 8 : 5, top = [84, 86, 88, 91, 93, 96, 98, 100];
      const t = this.begin(n + (big ? 3 : 0));
      if (t < 0) return;
      for (let i = 0; i < n; i++) {
        const m = top[Math.min(top.length - 1, i + ((Math.random() * 2) | 0))];
        this.tone(t, { wave: 'sine', f0: hz(m), at: delay + i * 0.045 + rand(0, 0.012), a: 0.003, d: 0.28, peak: 0.045 });
      }
      if (big) [72, 76, 79].forEach(m => this.tone(t, { wave: 'triangle', f0: hz(m), at: delay, a: 0.03, h: 0.15, d: 0.7, peak: 0.05 }));
    });
  }
  // UI click: tiny falling blip
  click(): void {
    this.run(() => {
      const t = this.begin(1);
      if (t < 0) return;
      this.tone(t, { wave: 'triangle', f0: 1500, f1: 900, a: 0.002, d: 0.035, peak: 0.08 });
    });
  }
  // purchase / unlock: rising jingle that lands on a held octave, then a sparkle
  success(): void {
    this.run(() => {
      const notes = [72, 76, 79, 84];
      const t = this.begin(notes.length + 1);
      if (t < 0) return;
      notes.forEach((m, i) => {
        const last = i === notes.length - 1;
        this.tone(t, { wave: 'triangle', f0: hz(m), at: i * 0.08, a: 0.005, h: last ? 0.12 : 0, d: last ? 0.5 : 0.14, peak: 0.085 });
      });
      this.tone(t, { wave: 'sine', f0: hz(96), at: 0.24, a: 0.01, h: 0.1, d: 0.5, peak: 0.04 });
      this.sparkle(false, 0.3);
    });
  }
  // countdown: short tick, final = higher and longer
  tick(final = false): void {
    this.run(() => {
      const t = this.begin(1);
      if (t < 0) return;
      this.tone(t, { wave: 'sine', f0: final ? 1320 : 880, a: 0.003, d: final ? 0.3 : 0.07, peak: final ? 0.12 : 0.08 });
    });
  }

  // ---------- engine ----------
  private run(fn: () => void): void {
    try { fn(); } catch { /* audio is a nicety: never break the game over it */ }
  }
  private read(k: string): string | null {
    try { return this.store ? this.store.getItem(k) : null; } catch { return null; }
  }
  private write(k: string, v: string): void {
    try { this.store?.setItem(k, v); } catch { /* private mode / storage blocked */ }
  }
  private init(): void {
    let c: AudioContext | null = null;
    try {
      c = this.makeCtx();
      if (!c) { this.failed = true; return; }
      // voices -> bus -> compressor (tames stacked sounds) -> master (volume / mute) -> speakers
      const bus = c.createGain(), comp = c.createDynamicsCompressor(), master = c.createGain();
      comp.threshold.value = -18; comp.knee.value = 12; comp.ratio.value = 4;
      comp.attack.value = 0.003; comp.release.value = 0.2;
      master.gain.value = this.level();
      bus.connect(comp); comp.connect(master); master.connect(c.destination);
      // one second of white noise, shared by every noisy sound
      const len = Math.max(1, Math.floor(c.sampleRate));
      const buf = c.createBuffer(1, len, c.sampleRate), ch = buf.getChannelData(0);
      for (let i = 0; i < len; i++) ch[i] = Math.random() * 2 - 1;
      // iOS only unlocks output once something plays inside the gesture: a 1-sample silent buffer
      const s = c.createBufferSource();
      s.buffer = c.createBuffer(1, 1, c.sampleRate);
      s.connect(c.destination);
      s.start(0);
      this.ctx = c; this.bus = bus; this.master = master; this.noiseBuf = buf;
    } catch {
      this.failed = true;
      try { void c?.close(); } catch { /* ignore */ }
    }
  }
  private level(): number { return this.isMuted ? 0 : this.vol; }
  private applyLevel(): void {
    const c = this.ctx, m = this.master;
    if (!c || !m) return;
    const t = c.currentTime;
    hold(m.gain, t);
    m.gain.linearRampToValueAtTime(this.level(), t + 0.05);
  }
  // the context is (or is about to be) running and we're audible
  private ready(): AudioContext | null {
    const c = this.ctx;
    if (!c || !this.bus || this.isMuted) return null;
    if (c.state === 'running') return c;
    return c.state === 'suspended' && nowMs() - this.resumedAt < RESUME_GRACE_MS ? c : null;
  }
  private resync(): void {
    if (this.boosting && !this.loop) this.startLoop();
  }
  // Reserve n voices; returns the start time, or -1 to skip the sound. Low-priority sounds (eat, eject)
  // are dropped when the cap is hit; others fade out the oldest voices to make room.
  private begin(n: number, low = false): number {
    const c = this.ready();
    if (!c) return -1;
    const now = c.currentTime;
    this.voices = this.voices.filter(v => v.end > now);
    const over = this.voices.length + n - MAX_VOICES;
    if (over > 0) {
      if (low) return -1;
      for (const v of this.voices.splice(0, over)) this.release(v, now);
    }
    return now + LEAD;
  }
  private release(v: Voice, now: number): void {
    try {
      hold(v.env.gain, now);
      v.env.gain.linearRampToValueAtTime(0, now + 0.015);
      v.src.stop(now + 0.02);
    } catch { /* already gone */ }
  }
  private tone(t: number, o: ToneOpts): void {
    const c = this.ctx!, start = t + (o.at ?? 0);
    const osc = c.createOscillator();
    osc.type = o.wave ?? 'sine';
    osc.frequency.setValueAtTime(clamp(o.f0, 20, 20000), start);
    if (o.f1 !== undefined) {
      osc.frequency.exponentialRampToValueAtTime(clamp(o.f1, 20, 20000), start + (o.sweep ?? this.span(o)));
    }
    if (o.detune) osc.detune.setValueAtTime(o.detune, start);
    this.voice(osc, start, o, o.filter);
  }
  private noise(t: number, o: NoiseOpts): void {
    const c = this.ctx!, start = t + (o.at ?? 0);
    const src = c.createBufferSource();
    src.buffer = this.noiseBuf;
    src.loop = true;
    this.voice(src, start, o, o.filter, Math.random() * 0.5);
  }
  private span(o: Env): number { return (o.a ?? 0.005) + (o.h ?? 0) + o.d; }
  // source -> optional filter -> envelope -> bus; schedules start/stop and tracks the voice
  private voice(src: AudioScheduledSourceNode, start: number, o: Env, f?: FilterOpts, offset?: number): void {
    const c = this.ctx!, env = c.createGain(), g = env.gain;
    const a = Math.max(0.002, o.a ?? 0.005), h = o.h ?? 0, end = start + a + h + o.d;
    const peak = Math.max(EPS * 2, o.peak);
    g.value = 0; // silent until its envelope starts, even if stolen before then
    g.setValueAtTime(0, start);
    g.linearRampToValueAtTime(peak, start + a);
    if (h > 0) g.setValueAtTime(peak, start + a + h);
    g.exponentialRampToValueAtTime(EPS, end);
    g.linearRampToValueAtTime(0, end + 0.01);
    if (f) {
      const bq = c.createBiquadFilter();
      bq.type = f.type;
      bq.Q.value = f.q ?? 0.7;
      bq.frequency.setValueAtTime(clamp(f.f0, 20, 18000), start);
      if (f.f1 !== undefined) bq.frequency.exponentialRampToValueAtTime(clamp(f.f1, 20, 18000), start + (f.sweep ?? a + h + o.d));
      src.connect(bq); bq.connect(env);
    } else src.connect(env);
    env.connect(this.bus!);
    if (offset !== undefined) (src as AudioBufferSourceNode).start(start, offset);
    else src.start(start);
    src.stop(end + 0.02);
    src.onended = () => { try { env.disconnect(); } catch { /* ignore */ } };
    this.voices.push({ end: end + 0.02, env, src });
  }
  // boost rumble: looping noise through a wobbling low-pass + a low saw, faded in and out
  private startLoop(): void {
    const c = this.ready();
    if (!c || this.loop || !this.noiseBuf) return;
    const t = c.currentTime + LEAD;
    const gain = c.createGain();
    gain.gain.value = 0;
    gain.gain.setValueAtTime(0, t);
    gain.gain.linearRampToValueAtTime(BOOST_LEVEL, t + 0.18);
    const n = c.createBufferSource();
    n.buffer = this.noiseBuf; n.loop = true;
    const lp = c.createBiquadFilter();
    lp.type = 'lowpass'; lp.frequency.value = 380; lp.Q.value = 1.2;
    const lfo = c.createOscillator(), depth = c.createGain();
    lfo.frequency.value = 6; depth.gain.value = 120;
    lfo.connect(depth); depth.connect(lp.frequency);
    const saw = c.createOscillator(), sawLp = c.createBiquadFilter(), sawGain = c.createGain();
    saw.type = 'sawtooth'; saw.frequency.value = 52;
    sawLp.type = 'lowpass'; sawLp.frequency.value = 170; sawLp.Q.value = 0.8;
    sawGain.gain.value = 0.55;
    n.connect(lp); lp.connect(gain);
    saw.connect(sawLp); sawLp.connect(sawGain); sawGain.connect(gain);
    gain.connect(this.bus!);
    n.start(t, Math.random() * 0.5); lfo.start(t); saw.start(t);
    this.loop = { gain, srcs: [n, lfo, saw] };
  }
  private stopLoop(): void {
    const l = this.loop, c = this.ctx;
    if (!l || !c) return;
    this.loop = null;
    const t = c.currentTime;
    hold(l.gain.gain, t);
    l.gain.gain.linearRampToValueAtTime(0, t + 0.25);
    for (const s of l.srcs) { try { s.stop(t + 0.3); } catch { /* ignore */ } }
    l.srcs[0].onended = () => { try { l.gain.disconnect(); } catch { /* ignore */ } };
  }
}
